import React, { useMemo, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Expand } from '@sparefoot/react-core';
import { PAGE_TYPES } from '@sparefoot/tracking-events';
import { TrackingClient } from '@sparefoot/tracking-client';
import { scrollToElement } from '@sparefoot/react-core/utils/scrolling';
import { change as changeFormData } from 'redux-form';

import { metaPropTypes, phonePropType } from 'config/propTypes';
import facilitySelector from 'store/selectors/facility';
import { getFacilityCardPhoneNumber } from 'store/modules/facilities';
import { createBooking } from 'store/modules/bookings';
import { onCreateReview } from 'store/modules/reviews';
import usePageViewTracking from 'hooks/usePageViewTracking';
import useSearchHandler from 'hooks/useSearchHandler';
import ErrorPage from 'pages/ErrorPage/ErrorPage';
import { LoadingPage } from 'components/core/LoadingPage';
import OGFFacilityPage from 'pages/OGFFacilityPage/OGFFacilityPage';
import { H2 } from 'components/core/Typography';
import { Page, Section, Container } from 'components/core/Page';
import {
	FacilityBreadcrumbs,
	FacilityGallery,
	HowItWorks,
	TopBanner,
	StorageNearYou,
} from 'components/organisms';
import FacilityReviews from 'components/organisms/FacilityPage/FacilityReviews/FacilityReviews';
import { ReviewForm } from 'components/review/ReviewForm';
import { ReviewTabs, GoogleReviewCard } from 'components/molecules';
import { SelfStorageSchema } from 'components/facility/SelfStorageSchema';
import { WebPageSchema } from 'components/facility/WebPageSchema';
import { WebSiteSchema } from 'components/facility/WebSiteSchema';
import { UnitProductSchema } from 'components/facility/UnitProductSchema';
import { FacilityDetails } from 'components/organisms/FacilityPage/FacilityDetails/FacilityDetails';
import FacilityAmenitiesRanked from 'components/facility/FacilityAmenitiesRanked/FacilityAmenitiesRanked';
import { FacilityUnitListV2 } from 'components/facility/FacilityUnitListV2/FacilityUnitListV2';
import { FeaturesAmenitiesAccordion } from 'components/facility/FacilityAmenitiesAccordion/FacilityAmenitiesAccordion';
import OMIIcon from 'assets/images/icons/omi-Icon.svg';
import Coupon from 'assets/images/icons/coupon.svg';
import questionMarkIcon from 'assets/images/icons/icon-question-mark.svg';

import './FacilityPageV2.scss';

function computeDiscounts(itemsObj, perRow = 3) {
	if (!itemsObj) return { discountItems: [], rows: [] };

	const discountItems = Object.entries(itemsObj)
		.filter(([, value]) => value?.hasAttribute === true)
		.map(([key, value]) => ({ key, label: value.label }));

	const rows = [];
	for (let i = 0; i < discountItems.length; i += perRow) {
		rows.push(discountItems.slice(i, i + perRow));
	}

	return { discountItems, rows };
}

const EMPTY = {};

export function FacilityPageV2({
	facility,
	phone,
	reviews,
	reviewPageNum,
	reviewSource,
	baseUrl,
	storageUnits,
	vehicleUnits,
	loading,
	trackingPixel,
	meta,
	isNewSearchPage,
	isGoogleReviewEnabled,
	nearbyFacilities,
	googleReviews,
	scarcityShow,
	statusCode: statusCodeProp,
	match,
}) {
	const dispatch = useDispatch();
	const ownProps = useMemo(() => ({ match }), [match]);
	const selectorResult =
		useSelector((state) => facilitySelector(state, ownProps)) || EMPTY;
	const {
		reviews: storeReviews,
		reviewPageNum: storeReviewPageNum,
		reviewSource: storeReviewSource,
		baseUrl: storeBaseUrl,
		storageUnits: storeStorageUnits,
		vehicleUnits: storeVehicleUnits,
		loading: storeLoading,
		trackingPixel: storeTrackingPixel,
		meta: storeMeta,
		scarcityShow: storeScarcityShow,
		isNewSearchPage: storeIsNewSearchPage,
		isGoogleReviewEnabled: storeIsGoogleReviewEnabled,
		nearbyFacilities: storeNearbyFacilities,
		googleReviews: storeGoogleReviews,
		facility: storeFacility,
		phone: storePhone,
	} = selectorResult || {};

	const resolvedFacility = facility ?? storeFacility;
	const resolvedPhone = phone ?? storePhone;
	const resolvedReviews = reviews ?? storeReviews;
	const resolvedReviewPageNum = reviewPageNum ?? storeReviewPageNum;
	const resolvedReviewSource = reviewSource ?? storeReviewSource;
	const resolvedBaseUrl = baseUrl ?? storeBaseUrl;
	const resolvedStorageUnits = storageUnits ?? storeStorageUnits ?? [];
	const resolvedVehicleUnits = vehicleUnits ?? storeVehicleUnits ?? [];
	const resolvedLoading = loading ?? storeLoading;
	const resolvedTrackingPixel = trackingPixel ?? storeTrackingPixel;
	const resolvedMeta = meta ?? storeMeta ?? {};
	const resolvedScarcityShow = scarcityShow ?? storeScarcityShow;
	const resolvedIsNewSearchPage = isNewSearchPage ?? storeIsNewSearchPage;
	const resolvedIsGoogleReviewEnabled =
		isGoogleReviewEnabled ?? storeIsGoogleReviewEnabled;
	const resolvedNearbyFacilities = nearbyFacilities ?? storeNearbyFacilities;
	const resolvedGoogleReviews = googleReviews ?? storeGoogleReviews;
	const statusCode = statusCodeProp ?? selectorResult?.statusCode;

	// Replaces withShowHide('description'), withShowHide('amenities'), withShowHide('facilityPhone') HOCs
	const [descriptionShowing, setDescriptionShowing] = useState(false);
	const [amenitiesShowing, setAmenitiesShowing] = useState(false);
	const [facilityPhoneShowing, setFacilityPhoneShowing] = useState(false);
	const onToggleDescription = useCallback(
		() => setDescriptionShowing((s) => !s),
		[],
	);
	const onToggleAmenities = useCallback(
		() => setAmenitiesShowing((s) => !s),
		[],
	);
	const onShowFacilityPhone = useCallback(
		() => setFacilityPhoneShowing(true),
		[],
	);

	// Replaces withSearchHandler HOC — provides handleSearch for OGFFacilityPage
	const handleSearch = useSearchHandler();

	// Replaces withPageViewTracking HOC
	usePageViewTracking(PAGE_TYPES.FACILITY);

	const actions = useMemo(
		() => ({
			changeFormData: (...args) => dispatch(changeFormData(...args)),
			createBooking: (...args) => dispatch(createBooking(...args)),
			onCreateReview: (...args) => dispatch(onCreateReview(...args)),
			getFacilityPhoneNumber: (...args) =>
				dispatch(getFacilityCardPhoneNumber(...args)),
		}),
		[dispatch],
	);

	const scrollToReviews = useCallback(() => scrollToElement('#reviews'), []);

	const [reviewFormShowing, setReviewFormShowing] = useState(false);
	useEffect(() => {
		if (!resolvedFacility?.inNetwork) return;
		TrackingClient.trackFacilityViewed({
			facility_id: resolvedFacility.id,
			facility_name: resolvedFacility.name,
			facility_promo: resolvedFacility.promo,
			facility_rank: resolvedFacility.rank,
		});
	}, [resolvedFacility?.id]);

	const { noIndex } = resolvedMeta;
	const { discountItems: availableDiscounts, rows: discountRows } =
		useMemo(() => {
			const items =
				resolvedFacility?.groupedAmenities?.discounts?.items || null;
			return computeDiscounts(items, 3);
		}, [resolvedFacility?.groupedAmenities?.discounts?.items]);

	const hasGoogleReviews =
		resolvedIsGoogleReviewEnabled &&
		resolvedGoogleReviews?.reviews &&
		Array.isArray(resolvedGoogleReviews.reviews) &&
		resolvedGoogleReviews.reviews.length > 0;
	const hasVerifiedReviews =
		Array.isArray(resolvedReviews) && resolvedReviews.length > 0;

	const renderReviewsSection = () => {
		if (hasGoogleReviews && hasVerifiedReviews) {
			return (
				<ReviewTabs
					verifiedCount={resolvedReviews.length}
					googleCount={resolvedGoogleReviews.user_ratings_total}
					verifiedContent={
						<FacilityReviews
							reviews={resolvedReviews}
							facility={resolvedFacility}
							reviewPageNum={resolvedReviewPageNum}
							baseUrl={resolvedBaseUrl}
							source={resolvedReviewSource}
							onCreateReview={actions.onCreateReview}
						/>
					}
					googleContent={renderGoogleReviewCardList()}
				/>
			);
		}
		if (hasGoogleReviews) {
			return (
				<>
					<Expand showing={reviewFormShowing}>
						<div>
							<ReviewForm
								onSubmit={actions.onCreateReview}
								source={resolvedReviewSource}
								facilityId={resolvedFacility.id}
							/>
						</div>
					</Expand>
					{renderGoogleReviewCardList()}
				</>
			);
		}
		return (
			<FacilityReviews
				reviews={resolvedReviews}
				facility={resolvedFacility}
				reviewPageNum={resolvedReviewPageNum}
				baseUrl={resolvedBaseUrl}
				source={resolvedReviewSource}
				onCreateReview={actions.onCreateReview}
			/>
		);
	};

	const renderGoogleReviewCardList = () => (
		<div className="google-review-card-list">
			{resolvedGoogleReviews.reviews.map((review) => (
				<GoogleReviewCard
					key={review.author_name + review.time}
					reviewerName={review.author_name}
					reviewerPhoto={review.profile_photo_url}
					rating={review.rating}
					reviewDate={review.time}
					reviewText={review.text}
				/>
			))}
			{resolvedGoogleReviews.placeId && (
				<div className="google-review-cta">
					<a
						href={`https://www.google.com/maps/search/?api=1&query=Google&query_place_id=${resolvedGoogleReviews.placeId}`}
						target="_blank"
						rel="noopener noreferrer"
						className="google-review-cta__button"
					>
						View all reviews on Google
					</a>
				</div>
			)}
		</div>
	);

	// Replaces withErrorHandler HOC
	if (statusCode > 200) {
		return <ErrorPage statusCode={statusCode} />;
	}

	// Replaces withRouteHandler((props) => !props.facility?.location?.address)
	if (!resolvedFacility?.location?.address) {
		return <LoadingPage />;
	}

	if (!resolvedFacility?.inNetwork) {
		return (
			<OGFFacilityPage
				facility={resolvedFacility}
				phone={resolvedPhone}
				reviews={resolvedReviews}
				reviewPageNum={resolvedReviewPageNum}
				reviewSource={resolvedReviewSource}
				baseUrl={resolvedBaseUrl}
				storageUnits={resolvedStorageUnits}
				vehicleUnits={resolvedVehicleUnits}
				loading={resolvedLoading}
				trackingPixel={resolvedTrackingPixel}
				meta={resolvedMeta}
				scarcityShow={resolvedScarcityShow}
				isNewSearchPage={resolvedIsNewSearchPage}
				isGoogleReviewEnabled={resolvedIsGoogleReviewEnabled}
				scrollToReviews={scrollToReviews}
				nearbyFacilities={resolvedNearbyFacilities}
				googleReviews={resolvedGoogleReviews}
				handleSearch={handleSearch}
				facilityPhoneShowing={facilityPhoneShowing}
				onShowFacilityPhone={onShowFacilityPhone}
				amenitiesShowing={amenitiesShowing}
				descriptionShowing={descriptionShowing}
				onToggleDescription={onToggleDescription}
				onToggleAmenities={onToggleAmenities}
				actions={actions}
			/>
		);
	}
	return (
		<Page
			className="facility-page-v2"
			trackingPixel={resolvedTrackingPixel}
			pageType={PAGE_TYPES.FACILITY}
			title={resolvedMeta.title}
			description={resolvedMeta.description}
			canonical={resolvedMeta.canonical}
			noIndex={noIndex}
			noFollow={noIndex}
		>
			<SelfStorageSchema
				facility={resolvedFacility}
				phone={resolvedPhone}
				baseUrl={resolvedBaseUrl}
				units={{ storageUnits: resolvedStorageUnits }}
				googleReviews={resolvedGoogleReviews}
			/>
			<WebPageSchema
				facility={resolvedFacility}
				baseUrl={resolvedBaseUrl}
			/>
			<WebSiteSchema />
			<UnitProductSchema
				units={[...resolvedStorageUnits, ...resolvedVehicleUnits]}
				facility={resolvedFacility}
				baseUrl={resolvedBaseUrl}
			/>
			<Section className="facility-header-section">
				<Container className="facility-header">
					<FacilityBreadcrumbs
						facility={resolvedFacility}
						baseUrl={resolvedBaseUrl}
					/>
				</Container>
				{!resolvedFacility.isVisible ? (
					<Container className="facility-banner-visible">
						<TopBanner
							className="top-banner__promo"
							content="Online reservations aren't available through SelfStorage. Please contact the facility directly to book."
						/>
					</Container>
				) : (
					<Container>
						<TopBanner
							className="top-banner__promo"
							title="Reserve Now!"
							content="Pricing and availability may change."
						/>
					</Container>
				)}
				<FacilityDetails
					facility={resolvedFacility}
					reviews={resolvedReviews}
					numberOfRatings={resolvedFacility.reviews?.number}
					phone={resolvedFacility.isVisible ? resolvedPhone : null}
					facilityUrl={resolvedFacility.url}
					googleReviews={resolvedGoogleReviews}
					isGoogleReviewEnabled={resolvedIsGoogleReviewEnabled}
					scrollToReviews={scrollToReviews}
					showSchema={false}
				/>
			</Section>
			<FacilityGallery
				facility={resolvedFacility}
				facilities={resolvedNearbyFacilities}
				baseUrl={resolvedBaseUrl}
			/>
			<Container>
				<Section className="facility-amenities-ranked">
					<Section className="section-heading">
						<H2
							size="large"
							weight="extraBold"
							last
						>
							Key Features
						</H2>
					</Section>
					<FacilityAmenitiesRanked
						groupedAmenities={resolvedFacility.groupedAmenities}
						storageUnits={resolvedStorageUnits}
						vehicleUnits={resolvedVehicleUnits}
					/>
				</Section>
			</Container>
			{resolvedFacility.isVisible && (
				<>
					<Section className="facility-units-section">
						<Container>
							<Section>
								{(resolvedStorageUnits.length > 0 ||
									resolvedVehicleUnits.length > 0) &&
									(availableDiscounts?.length > 0 ||
										resolvedFacility?.hasOnlineMoveins) && (
										<section className="discount-banners">
											{resolvedFacility?.hasOnlineMoveins && (
												<section className="content-container">
													<div className="discount-row discount-row--1">
														<TopBanner
															className="top-banner_omi"
															iconAlt="Online Move In Icon"
															icon={OMIIcon}
															title="Online Move-In Available"
															content="Rent online, skip the counter, and go straight to your space"
														/>
													</div>
												</section>
											)}

											{availableDiscounts.length > 0 && (
												<section className="content-container">
													{discountRows.map((row) => (
														<div
															className={`discount-row discount-row--${row.length}`}
															key={row
																.map(
																	(d) =>
																		d.key,
																)
																.join('-')}
														>
															{row.map(
																({
																	key,
																	label,
																}) => (
																	<div
																		className="discount-item"
																		key={
																			key
																		}
																	>
																		<TopBanner
																			className="top-banner_discount"
																			title={label
																				.split(
																					'(',
																				)[0]
																				.trim()}
																			icon={
																				Coupon
																			}
																			iconAlt="Discount Icon"
																			tooltipIcon={
																				questionMarkIcon
																			}
																			tooltipMessage={
																				label
																			}
																		/>
																	</div>
																),
															)}
														</div>
													))}
												</section>
											)}
										</section>
									)}
							</Section>
							<FacilityUnitListV2
								facility={resolvedFacility}
								storageUnits={[
									...resolvedStorageUnits,
									...resolvedVehicleUnits,
								]}
								vehicleUnits={resolvedVehicleUnits}
								changeFormData={actions.changeFormData}
								onCreateBooking={actions.createBooking}
								isNewSearchPage={resolvedIsNewSearchPage}
								loading={resolvedLoading}
							/>
						</Container>
					</Section>
					<Section>
						<Container>
							<FeaturesAmenitiesAccordion
								facility={resolvedFacility}
								title="Facility Overview & What to Expect"
							/>
						</Container>
					</Section>
					<Section
						id="reviews"
						className="facility-units-section"
					>
						<Container>
							<div className="reviews-heading-wrapper">
								<H2
									size="large"
									weight="extraBold"
									className="reviews-heading"
								>
									Verified Customer Reviews
								</H2>
								{hasGoogleReviews && !hasVerifiedReviews && (
									<button
										type="button"
										className="leave-review-link"
										onClick={() =>
											setReviewFormShowing(
												(prev) => !prev,
											)
										}
									>
										Leave a review of this facility
									</button>
								)}
							</div>
							{renderReviewsSection()}
						</Container>
					</Section>
					<HowItWorks />
				</>
			)}
			<Section className="facility-nearby-facilities">
				<StorageNearYou type="Facility" />
			</Section>
		</Page>
	);
}

FacilityPageV2.propTypes = {
	baseUrl: PropTypes.string,
	facility: PropTypes.object,
	googleReviews: PropTypes.object,
	isGoogleReviewEnabled: PropTypes.bool,
	isNewSearchPage: PropTypes.bool,
	loading: PropTypes.bool,
	match: PropTypes.object,
	meta: PropTypes.shape(metaPropTypes),
	nearbyFacilities: PropTypes.arrayOf(PropTypes.object),
	phone: phonePropType,
	reviewPageNum: PropTypes.number,
	reviews: PropTypes.arrayOf(PropTypes.object),
	reviewSource: PropTypes.string,
	scarcityShow: PropTypes.bool,
	statusCode: PropTypes.number,
	storageUnits: PropTypes.arrayOf(PropTypes.object),
	trackingPixel: PropTypes.string,
	vehicleUnits: PropTypes.arrayOf(PropTypes.object),
};

export default FacilityPageV2;
